import { Injectable, inject } from '@angular/core';

import { filter, map, switchMap, tap } from 'rxjs';

import { GraphEndpoint, HandleSimpleRequest } from '@ta/server';
import { TaPermissionsService, TaUserService } from '@ta/user';
import { isNonNullable } from '@ta/utils';

import { estateProps } from 'src/app/services/estates/dto/estate';

import { ECompanyRole, companyMemberProps } from '../company/dto/company-member';
import { companyProps } from '../company/dto/company';
import { IdentityVerification } from './dto/identity-verification';
import { IdentityVerificationPayload } from './dto/identity-verification-payload';
import { ownerProps } from './dto/owner';
import { tenantProps } from './dto/tenant';
import { UserProfile, UserStats, userProfileProps } from './dto/user';
import { UpdateUserInput } from './form/dto/updateUserInput';
import { initiateIdentityVerification, updateUser, updateUserPartial } from './mutation';
import { myIdentityVerificationQuery, userInfoQuery } from './queries';

type StatsRaw = {
  tenant?: { tenantReviews: { id: string }[]; globalNote: number | null };
  owner?: {
    estates: { id: string; tenantApplications?: { ownerReview?: { id: string } | null }[] }[];
    globalNote: number | null;
  };
};

const graphEndpoint: GraphEndpoint = {
  clientName: 'userService',
  endpoint: '',
};

@Injectable({
  providedIn: 'root',
})
export class UserService<T = UserProfile> extends TaUserService<T> {
  private readonly _permissionService = inject(TaPermissionsService);

  public statistique = new HandleSimpleRequest<UserStats>();
  public userDetail = new HandleSimpleRequest<UserProfile>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public override fetchUserProfile$(_?: unknown) {
    return super
      .fetchUserProfile$(
        `
        ${userProfileProps.get('bailoId')}
        ${userProfileProps.get('trustScore')}
        ${userProfileProps.get('profileCompleted')}
        ${userProfileProps.get('status')}
        ${userProfileProps.get('preferredLanguage')}
        ${userProfileProps.get('emailNotificationsEnabled')}
        ${userProfileProps.get('pushNotificationsEnabled')}
        ${userProfileProps.get('visitBufferMinutes')}
        ${userProfileProps.get('tenant')} {
          ${tenantProps.get('id')}
        }
        ${userProfileProps.get('owner')} {
          ${ownerProps.get('id')}
        }
        ${userProfileProps.get('companyMembership')} {
          ${companyMemberProps.get('id')}
          ${companyMemberProps.get('role')}
          ${companyMemberProps.get('companyId')}
          ${companyMemberProps.get('company')} {
            ${companyProps.get('id')}
            ${companyProps.get('name')}
            ${companyProps.get('logo')}
          }
        }
      `,
      )
      .pipe(
        tap((user) => {
          const profile = user as unknown as UserProfile;
          const isOwner = !!profile?.owner?.id;
          const isTenant = !!profile?.tenant?.id;
          const isCompanyMember = !!profile?.companyMembership?.id;
          const isCompanyAdmin =
            isCompanyMember && profile.companyMembership?.role === ECompanyRole.Admin;

          const roles: string[] = [];
          if (isOwner) {
            roles.push('owner');
          }
          if (isTenant) {
            roles.push('tenant');
          }
          if (isCompanyMember) {
            roles.push('company');
          }
          if (isCompanyAdmin) {
            roles.push('companyAdmin');
          }

          // Les routes se gardent par feature (`FeatureGuard`), pas par combinaison de rôles.
          const features: string[] = [];
          if (isOwner || isCompanyMember) {
            features.push('estates', 'subscription', 'visitAvailability');
          }
          if (isCompanyMember) {
            features.push('company');
          }
          if (isOwner || isCompanyAdmin) {
            features.push('invitations');
          }
          // Tout ce qui est réservé aux locataires (recommandations, recherches sauvegardées).
          if (isTenant) {
            features.push('tenant');
          }

          this._permissionService.setGuard({ features, roles });
        }),
      );
  }

  public fetchDetails$() {
    return this.userDetail.fetch(
      this._graphService
        .fetchQueryBuilder<UserProfile>(
          userInfoQuery({
            props: `
              ${userProfileProps.get('id')}
              ${userProfileProps.get('bailoId')}
              ${userProfileProps.get('dateOfBirth')}
              ${userProfileProps.get('email')}
              ${userProfileProps.get('fieldVisibilities')} { fieldName isPublic }
              ${userProfileProps.get('firstname')}
              ${userProfileProps.get('lastname')}
              ${userProfileProps.get('picture')}
              ${userProfileProps.get('status')}
              ${userProfileProps.get('tenant')} {
                ${tenantProps.get('id')}
                ${tenantProps.get('animals')}
                ${tenantProps.get('cpas')}
                ${tenantProps.get('description')}
                ${tenantProps.get('insurance')}
                ${tenantProps.get('smoking')}
                ${tenantProps.get('vehicle')}
                ${tenantProps.get('revenue')}
              }
              ${userProfileProps.get('owner')} {
                ${ownerProps.get('id')}
                ${ownerProps.get('description')}
                ${ownerProps.get('revenue')}
                ${ownerProps.get('estates')} {
                  ${estateProps.get('id')}
                  ${estateProps.get('name')}
                  ${estateProps.get('description')}
                  ${estateProps.get('status')}
                  ${estateProps.get('available')}
                  ${estateProps.get('floorArea')}
                  ${estateProps.get('rooms')}
                  ${estateProps.get('bedrooms')}
                  ${estateProps.get('floor')}
                  ${estateProps.get('serviceCharges')}
                  ${estateProps.get('securityDeposit')}
                  ${estateProps.get('insurance')}
                  ${estateProps.get('createdDate')}
                  ${estateProps.get('updatedDate')}
                  ${estateProps.get('addressId')}
                }
              }
            `,
          }),
          graphEndpoint.clientName,
        )
        .pipe(filter(isNonNullable)),
    );
  }

  public fetchStatistique$() {
    return this.statistique.fetch(
      this._graphService
        .fetchQueryBuilder<StatsRaw>(
          userInfoQuery({
            props: `
              tenant {
                tenantReviews { id }
                globalNote
              }
              owner {
                estates {
                  id
                  tenantApplications {
                    ownerReview { id }
                  }
                }
                globalNote
              }
            `,
          }),
          graphEndpoint.clientName,
        )
        .pipe(
          filter(isNonNullable),
          map(
            (data): UserStats => ({
              // Reçues comme locataire et comme propriétaire : un avis sur le bailleur par bail.
              totalReviews:
                (data.tenant?.tenantReviews?.length ?? 0) +
                (data.owner?.estates ?? []).reduce(
                  (count, estate) =>
                    count +
                    (estate.tenantApplications ?? []).filter((ta) => !!ta.ownerReview).length,
                  0,
                ),
              totalEstates: data.owner?.estates?.length ?? 0,
              ownerGlobalNote: data.owner?.globalNote ?? null,
              tenantGlobalNote: data.tenant?.globalNote ?? null,
            }),
          ),
        ),
    );
  }

  public syncPreferredLanguage$(lang: string) {
    return this._graphService
      .mutate<UserProfile>(
        updateUser({ preferredLanguage: lang }),
        'updateUser',
        graphEndpoint.clientName,
        ['userInfo'],
      )
      .pipe(filter(isNonNullable));
  }

  /** Mise à jour partielle des flags de notification du compte, puis profil rechargé. */
  public updateNotificationPreferences$(
    input: Partial<Pick<UpdateUserInput, 'emailNotificationsEnabled' | 'pushNotificationsEnabled'>>,
  ) {
    return this._graphService
      .mutateBuilder<UserProfile>(updateUserPartial(input), graphEndpoint.clientName, ['userInfo'])
      .pipe(switchMap(() => this.fetchUserProfile$()));
  }

  public updateDetails$(user: Partial<UpdateUserInput>) {
    return this._graphService
      .mutate<UserProfile>(updateUser(user), 'updateUser', graphEndpoint.clientName, ['userInfo'])
      .pipe(
        filter(isNonNullable),
        switchMap((_) => this.fetchUserProfile$(_)),
      );
  }

  public initiateIdentityVerification$(lang?: string) {
    return this._graphService
      .mutate<IdentityVerificationPayload>(
        initiateIdentityVerification(lang),
        'initiateIdentityVerification',
        graphEndpoint.clientName,
        ['userInfo', 'myIdentityVerification'],
      )
      .pipe(filter(isNonNullable));
  }

  public fetchMyIdentityVerification$() {
    return this._graphService
      .fetchQueryBuilder<IdentityVerification>(
        myIdentityVerificationQuery(),
        graphEndpoint.clientName,
      )
      .pipe(filter(isNonNullable));
  }
}
